import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { FaUsers, FaUser, FaSearch, FaTimes, FaCrown, FaUserShield } from "react-icons/fa";
import { useMembers } from "@/api/account";
import { Member, Role } from "@/models/account";

/**
 * MembersSection component - displays organization members in the sidebar.
 * Shows all members with search functionality and role indicators.
 */
const MembersSection: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: members = [] } = useMembers();

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter members by search
  const filteredMembers = useMemo(() => {
    if (!searchQuery) return members;
    const query = searchQuery.toLowerCase();
    return members.filter((member) => {
      const userName = typeof member.user === "string" ? member.user : member.user?.name || member.user?.email || "";
      return userName.toLowerCase().includes(query);
    });
  }, [members, searchQuery]);

  // Sort: admins first, then by name
  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      const roleOrder: Record<Role | string, number> = { admin: 0, editor: 1, viewer: 2 };
      const aOrder = roleOrder[a.role] ?? 3;
      const bOrder = roleOrder[b.role] ?? 3;
      if (aOrder !== bOrder) return aOrder - bOrder;
      
      const aName = typeof a.user === "string" ? a.user : a.user?.name || "";
      const bName = typeof b.user === "string" ? b.user : b.user?.name || "";
      return aName.localeCompare(bName);
    });
  }, [filteredMembers]);

  // Handle member click - navigate to members page
  const handleMemberClick = useCallback(
    (member: Member) => {
      if (!location.pathname.startsWith("/members")) {
        navigate("/members");
      }
    },
    [navigate, location.pathname]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    searchInputRef.current?.focus();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f" && members.length > 0) {
        if (searchInputRef.current) {
          e.preventDefault();
          searchInputRef.current.focus();
        }
      }
      if (e.key === "Escape" && isSearchFocused) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSearchFocused, members.length]);

  // Get role icon and color
  const getRoleConfig = (role: Role | string) => {
    switch (role) {
      case "admin":
        return { icon: FaCrown, color: "text-warning", label: "Admin" };
      case "editor":
        return { icon: FaUserShield, color: "text-accent", label: "Editor" };
      default:
        return { icon: FaUser, color: "text-content-tertiary", label: "Viewer" };
    }
  };

  // Get member display name
  const getMemberName = (member: Member) => {
    if (typeof member.user === "string") return member.user;
    return member.user?.name || member.user?.email || "Unknown";
  };

  if (members.length === 0) {
    return (
      <div className="px-3 py-4 text-center text-[var(--arc-sidebar-text-secondary)]">
        <span className="text-xs">Aucun membre</span>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      {/* Section Title */}
      <div className="flex items-center gap-2 px-2 mb-2 text-[var(--arc-accent)]">
        <FaUsers size={10} />
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          Members
        </span>
        <div className="flex-1 h-px bg-[var(--arc-sidebar-border-accent)]" />
        <span className="text-[10px] text-[var(--arc-sidebar-text-secondary)]">
          {filteredMembers.length}
        </span>
      </div>

      {/* Search Bar */}
      {members.length > 3 && (
        <div className="px-1 mb-3">
          <div
            className={`relative flex items-center rounded-md transition-all duration-200 ${
              isSearchFocused
                ? "bg-[var(--arc-sidebar-accent-glow)] ring-1 ring-[var(--arc-accent)]"
                : "bg-[var(--arc-sidebar-accent-glow)] hover:bg-[rgba(var(--arc-accent-rgb),0.15)]"
            }`}
          >
            <FaSearch
              className={`absolute left-2.5 transition-colors duration-200 ${
                isSearchFocused ? "arc-sidebar-icon-active" : ""
              }`}
              size={11}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              placeholder="Rechercher..."
              className="w-full bg-transparent text-[var(--arc-sidebar-text-primary)] text-xs py-1.5 pl-7 pr-7 placeholder-[var(--arc-sidebar-text-secondary)] focus:outline-none focus:ring-0"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 p-0.5 rounded hover:bg-[var(--arc-sidebar-accent-glow)] transition-colors text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)]"
              >
                <FaTimes size={10} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="space-y-0.5">
        {sortedMembers.map((member) => {
          const roleConfig = getRoleConfig(member.role);
          const RoleIcon = roleConfig.icon;
          
          return (
            <button
              key={member.id}
              onClick={() => handleMemberClick(member)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-all text-[var(--arc-sidebar-text-secondary)] hover:bg-[var(--arc-sidebar-accent-glow)] hover:text-[var(--arc-sidebar-text-primary)]"
            >
              <FaUser size={10} className="text-[var(--arc-accent)]" />
              <span className="text-xs truncate flex-1">{getMemberName(member)}</span>
              <RoleIcon size={8} className={roleConfig.color} title={roleConfig.label} />
            </button>
          );
        })}
      </div>

      {/* No results */}
      {filteredMembers.length === 0 && searchQuery && (
        <div className="px-2 py-3 text-center text-[var(--arc-sidebar-text-secondary)]">
          <span className="text-xs">Aucun résultat</span>
        </div>
      )}
    </div>
  );
};

MembersSection.displayName = "MembersSection";

export default MembersSection;
